'use client';

/**
 * ChatBubble Component
 *
 * REQUIREMENTS SUMMARY:
 * - Desktop (sm+): Badge-style button fixed top-right below the app header for visibility
 * - Mobile (<sm): Circle FAB fixed bottom-right for thumb reachability
 * - Both hidden when chat window is open (ChatWindow has its own close button)
 */

import React from 'react';
import { motion } from 'framer-motion';
import AssistantRobotIcon from './AssistantRobotIcon';
import { cn } from '@/utils/common/helpers';

interface ChatBubbleProps {
  isOpen: boolean;
  onClick: () => void;
  unreadCount?: number;
  className?: string;
}

export function ChatBubble({
  isOpen,
  onClick,
  unreadCount = 0,
  className,
}: ChatBubbleProps) {
  // Hide entirely when chat is open (ChatWindow has its own close/toggle)
  if (isOpen) return null;

  return (
    <>
      {/* Desktop: Badge-style button fixed below the app header */}
      <motion.button
        onClick={onClick}
        className={cn(
          'fixed right-6 z-[9999]',
          'hidden sm:inline-flex items-center gap-2',
          'rounded-xl bg-gradient-to-r from-brand-600 to-purple-600',
          'px-4 py-2.5 text-sm font-semibold text-white',
          'shadow-lg hover:shadow-xl transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
          'dark:focus:ring-offset-gray-900',
          className
        )}
        style={{ top: 'calc(var(--app-header-height, 72px) + 12px)' }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        aria-label="Open chat assistant"
      >
        <AssistantRobotIcon className="w-8 h-[26px] -my-[3px] text-white" />
        CheerVue Assistant
        {unreadCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-medium rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </motion.button>

      {/* Mobile: Circle FAB at bottom-right */}
      <motion.button
        onClick={onClick}
        className={cn(
          'fixed bottom-6 right-6 z-[9999]',
          'sm:hidden',
          'w-14 h-14 rounded-full',
          'flex items-center justify-center',
          'bg-gradient-to-r from-brand-600 to-purple-600 text-white',
          'shadow-2xl transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open chat assistant"
      >
        <AssistantRobotIcon className="w-[42px] h-[31px] text-white" />
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.div>
        )}
      </motion.button>
    </>
  );
}

export default ChatBubble;
